import { SearchOutlined } from '@mui/icons-material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ButtonCustom from 'components/ButtonCustom';
import ButtonIcon from 'components/ButtonIcon';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import { InspectionStatusBadge } from 'components/RevisionRowData';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import { debounce } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getIdfDownloadLogsCSV } from 'services/admin-organizations';
import {
  getAdminInspectionFilterOrganizations,
  getAdminInspectionsList,
  InspectionFilterOrgOption
} from 'services/inspections-service';
import { getErrorHtmlContent } from 'utils/utils';

const statusFilterOptions = (t: (key: string) => string) => [
  { value: '', label: t('ALL') || 'All' },
  { value: 'awaiting', label: t('REVISION_INSPECTION_STATUS_AWAITING') },
  { value: 'pending', label: t('REVISION_INSPECTION_STATUS_PENDING') },
  { value: 'checked', label: t('REVISION_INSPECTION_STATUS_CHECKED') },
];

export default function AdminInspectionsListPage() {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isRtl = i18n.language === 'he';
  const [loadingDownloadIdfLogs, setLoadingDownloadIdfLogs] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('updatedAt');

  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearchDebounced(value), 400),
    []
  );
  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => debouncedSetSearch.cancel();
  }, [searchInput, debouncedSetSearch]);

  const [sourceOrgInput, setSourceOrgInput] = useState('');
  const [sourceOrgDebounced, setSourceOrgDebounced] = useState('');
  const debouncedSourceInput = useMemo(
    () => debounce((value: string) => setSourceOrgDebounced(value), 300),
    []
  );
  useEffect(() => {
    debouncedSourceInput(sourceOrgInput);
    return () => debouncedSourceInput.cancel();
  }, [sourceOrgInput, debouncedSourceInput]);

  const [targetOrgInput, setTargetOrgInput] = useState('');
  const [targetOrgDebounced, setTargetOrgDebounced] = useState('');
  const debouncedTargetInput = useMemo(
    () => debounce((value: string) => setTargetOrgDebounced(value), 300),
    []
  );
  useEffect(() => {
    debouncedTargetInput(targetOrgInput);
    return () => debouncedTargetInput.cancel();
  }, [targetOrgInput, debouncedTargetInput]);

  const [selectedSourceOrg, setSelectedSourceOrg] = useState<InspectionFilterOrgOption | null>(null);
  const [selectedTargetOrg, setSelectedTargetOrg] = useState<InspectionFilterOrgOption | null>(null);

  const { data: sourceOrgOptions = [], isFetching: loadingSourceOrgs } = useQuery(
    ['admin-inspections-source-org-options', sourceOrgDebounced],
    () =>
      getAdminInspectionFilterOrganizations({
        type: 'source',
        search: sourceOrgDebounced,
        limit: 30,
      }),
    { keepPreviousData: true }
  );

  const { data: targetOrgOptions = [], isFetching: loadingTargetOrgs } = useQuery(
    ['admin-inspections-target-org-options', targetOrgDebounced],
    () =>
      getAdminInspectionFilterOrganizations({
        type: 'target',
        search: targetOrgDebounced,
        limit: 30,
      }),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, selectedSourceOrg?.id, selectedTargetOrg?.id]);

  const { data, isLoading, isFetching } = useQuery(
    [
      'admin-inspections-list',
      status,
      page,
      pageSize,
      sortBy,
      sortOrder,
      searchDebounced,
      selectedSourceOrg?.id,
      selectedTargetOrg?.id,
    ],
    () =>
      getAdminInspectionsList({
        status: status || undefined,
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: searchDebounced.trim() || undefined,
        sourceOrgId: selectedSourceOrg?.id,
        targetOrgId: selectedTargetOrg?.id,
      }),
    { keepPreviousData: true }
  );

  const list = data?.data ?? [];
  const paging = data?.paging;

  const allRows = useMemo(
    () =>
      list.map((it: any) => ({
        id: it._id || it.id,
        organizationName: it.organization?.company_name || '-',
        organizationImageUrl: it.organization?.image_url,
        sourceOrganizationName: it.targetOrganization?.company_name || '-',
        sourceOrganizationImageUrl: it.targetOrganization?.image_url,
        revisionId: it.revision?._id || it.revision?.id,
        projectId: it.project?._id || it.project?.id,
        projectName: it.project?.name || '-',
        projectType: it.revision?.project?.type || it.project?.type || '-',
        targetInspectorLabel:
          it.inspector?.name || it.inspector?.email || '-',
        updatedAt: it.updatedAt,
        inspection: it,
      })),
    [list]
  );

  const handleRequestSort = (_event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newOrderBy);
    setPage(1);
  };

  const openInspectionReport = (revisionId: string | undefined) => {
    if (!revisionId) return;
    window.open(
      `/report/${revisionId}/check`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const tableBusy = isFetching || isLoading;

  const handleDownloadIdfLogs = async () => {
    setLoadingDownloadIdfLogs(true);
    try {
      const response = await getIdfDownloadLogsCSV();
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `idf_download_logs_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    } finally {
      setLoadingDownloadIdfLogs(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#2F3B49' }}>
            {t('ADMIN_INSPECTIONS_LIST_TITLE')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Tooltip title={t('ADMIN_IDF_DOWNLOAD_LOGS_EXPORT')} placement="bottom">
              <span>
                <ButtonIcon onClick={handleDownloadIdfLogs} disabled={loadingDownloadIdfLogs}>
                  {loadingDownloadIdfLogs ? (
                    <CircularProgress size={24} />
                  ) : (
                    <FileDownloadOutlinedIcon fontSize="large" />
                  )}
                </ButtonIcon>
              </span>
            </Tooltip>
            <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel id="admin-inspections-status-label">
              {t('ADMIN_REVISION_JOB_STATUS')}
            </InputLabel>
            <Select
              labelId="admin-inspections-status-label"
              value={status}
              label={t('ADMIN_REVISION_JOB_STATUS')}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              {statusFilterOptions(t).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <TextField
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('ADMIN_INSPECTIONS_SEARCH_PLACEHOLDER')}
            sx={{ flex: '1 1 240px', minWidth: 200, maxWidth: 480 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            size="small"
            sx={{ flex: '1 1 220px', minWidth: 200 }}
            options={sourceOrgOptions}
            loading={loadingSourceOrgs}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o.company_name || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedSourceOrg}
            onChange={(_, v) => {
              setSelectedSourceOrg(v);
              setSourceOrgInput(v?.company_name ?? '');
              setPage(1);
            }}
            inputValue={sourceOrgInput}
            onInputChange={(_, v, reason) => {
              if (reason === 'reset') return;
              setSourceOrgInput(v);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('ADMIN_INSPECTIONS_FILTER_SOURCE_ORG')}
                placeholder={t('ADMIN_STATS_SEARCH_PLACEHOLDER')}
              />
            )}
          />
          <Autocomplete
            size="small"
            sx={{ flex: '1 1 220px', minWidth: 200 }}
            options={targetOrgOptions}
            loading={loadingTargetOrgs}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o.company_name || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedTargetOrg}
            onChange={(_, v) => {
              setSelectedTargetOrg(v);
              setTargetOrgInput(v?.company_name ?? '');
              setPage(1);
            }}
            inputValue={targetOrgInput}
            onInputChange={(_, v, reason) => {
              if (reason === 'reset') return;
              setTargetOrgInput(v);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('ADMIN_INSPECTIONS_FILTER_TARGET_ORG')}
                placeholder={t('ADMIN_STATS_SEARCH_PLACEHOLDER')}
              />
            )}
          />
        </Box>
      </Box>

      <Box sx={{ position: 'relative', minHeight: 200 }}>
        <TableContainer
          sx={{
            ...(tableBusy
              ? { opacity: 0.45, pointerEvents: 'none', transition: 'opacity 0.2s' }
              : {}),
          }}
        >
        <Table size="small" sx={{ fontSize: '14px' }}>
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
              <CustomTableFontCell>
                {t('ADMIN_INSPECTIONS_ORGANIZATION_SOURCE')}
              </CustomTableFontCell>
              <CustomTableFontCell>
                {t('INSPECTION_DETAILS_ORGANIZATION')}
              </CustomTableFontCell>
              <CustomTableFontCell>
                {t('ADMIN_INSPECTIONS_TARGET_INSPECTION')}
              </CustomTableFontCell>
              <CustomTableFontCell>
                {t('INSPECTION_PROJECT')}
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === 'revisionId' ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === 'revisionId'}
                  direction={sortBy === 'revisionId' ? sortOrder : 'asc'}
                  onClick={(e) => handleRequestSort(e, 'revisionId')}
                >
                  {t('REVISION_REVISION_ID_COLUMN')}
                  {sortBy === 'revisionId' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sx={{ width: '135px', maxWidth: '135px' }}>
                {t('ADMIN_REVISION_JOB_STATUS')}
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === 'updatedAt' ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === 'updatedAt'}
                  direction={sortBy === 'updatedAt' ? sortOrder : 'asc'}
                  onClick={(e) => handleRequestSort(e, 'updatedAt')}
                >
                  {t('INSPECTION_UPDATED_DATE')}
                  {sortBy === 'updatedAt' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!tableBusy && allRows.length === 0 ? (
              <TableRow>
                <CustomTableCell
                  colSpan={7}
                  align="center"
                  clickable={false}
                  sx={{ py: 4 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('NO_SEARCHRESULT_HEADING_1')}
                  </Typography>
                </CustomTableCell>
              </TableRow>
            ) : null}
            {allRows.map((r) => (
              <TableRow
                key={r.id}
                onClick={() => openInspectionReport(r.revisionId)}
                sx={{
                  cursor: r.revisionId ? 'pointer' : 'default',
                  '&:hover': { border: 'none' },
                }}
              >
                <CustomTableCell
                  sx={{
                    cursor: 'inherit',
                    userSelect: 'text',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {r.sourceOrganizationImageUrl && (
                      <Box
                        component="img"
                        src={r.sourceOrganizationImageUrl}
                        alt={r.sourceOrganizationName}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {r.sourceOrganizationName}
                    </Typography>
                  </Box>
                </CustomTableCell>
                <CustomTableCell
                  sx={{
                    cursor: 'inherit',
                    userSelect: 'text',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {r.organizationImageUrl && (
                      <Box
                        component="img"
                        src={r.organizationImageUrl}
                        alt={r.organizationName}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {r.organizationName}
                    </Typography>
                  </Box>
                </CustomTableCell>
                <CustomTableCell
                  sx={{ cursor: 'inherit', userSelect: 'text' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    {r.targetInspectorLabel}
                  </Typography>
                </CustomTableCell>
                <CustomTableCell
                  sx={{ cursor: 'inherit', userSelect: 'text' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    {r.projectName}
                  </Typography>
                </CustomTableCell>
                <CustomTableCell
                  sx={{ cursor: 'inherit', userSelect: 'text' }}
                  dir="ltr"
                >
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>
                    {r.revisionId}
                  </Typography>
                </CustomTableCell>
                <CustomTableCell
                  sx={{
                    cursor: 'inherit',
                    userSelect: 'text',
                    width: '135px',
                    maxWidth: '135px',
                  }}
                >
                  <InspectionStatusBadge rtl={isRtl} inspection={r.inspection} />
                </CustomTableCell>
                <CustomTableCell
                  sx={{ cursor: 'inherit', userSelect: 'text' }}
                >
                  {moment.utc(r.updatedAt).local().format('DD/MM/YY')}
                  <span className="time" style={{ marginLeft: '10px' }} />
                  {moment.utc(r.updatedAt).local().format('HH:mm')}
                </CustomTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
        {tableBusy ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.55)',
              zIndex: 1,
            }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : null}
      </Box>

      {paging && paging.total > pageSize && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              {t('MUI_PAGINATION_RANGE', {
                from: (page - 1) * pageSize + 1,
                to: Math.min(page * pageSize, paging.total),
                count: paging.total,
              })}
            </Typography>
            <ButtonCustom
              variant="outlined"
              size="small"
              disabled={page <= 1 || tableBusy}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t('MUI_PAGINATION_PREVIOUS_PAGE')}
            </ButtonCustom>
            <ButtonCustom
              variant="outlined"
              size="small"
              disabled={page * pageSize >= paging.total || tableBusy}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('MUI_PAGINATION_NEXT_PAGE')}
            </ButtonCustom>
          </Box>
        </Box>
      )}
    </Box>
  );
}
